
import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Edit, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Client } from '@/models/Client';
import { hasUnpaidSessions } from '@/utils/clientFilters';
import PaymentStatusButton from './PaymentStatusButton';

interface ClientActionsProps {
  client: Client;
  onEdit?: (client: Client) => void;
  onDelete?: (client: Client) => void;
  onViewUpcomingBookings?: (client: Client) => void;
  canManage?: boolean;
}

const ClientActions: React.FC<ClientActionsProps> = ({
  client,
  onEdit,
  onDelete,
  onViewUpcomingBookings,
  canManage = false,
}) => {
  const { t } = useTranslation();
  const clientHasUnpaidSessions = hasUnpaidSessions(client);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="flex flex-col gap-2 pt-3 border-t dark:border-gray-700">
      <div className="flex justify-center items-center gap-2">
        <Button
          onClick={(e) => {
            handleButtonClick(e);
            onViewUpcomingBookings?.(client);
          }}
          size="icon"
          variant="outline"
          title={t('clients.upcomingBookings')}
          className="dark:border-gray-600 dark:hover:bg-gray-800"
        >
          <Calendar className="h-4 w-4" />
        </Button>
        
        <div onClick={handleButtonClick}>
          <PaymentStatusButton 
            client={client} 
            size="icon" 
            variant={clientHasUnpaidSessions ? "destructive" : "outline"} 
          />
        </div>

        {canManage && (
          <Button
            onClick={(e) => {
              handleButtonClick(e);
              onEdit?.(client);
            }}
            size="icon"
            variant="outline"
            title={t('clients.edit')}
            className="dark:border-gray-600 dark:hover:bg-gray-800"
          >
            <Edit className="h-4 w-4" />
          </Button>
        )}

        {canManage && (
          <Button
            onClick={(e) => {
              handleButtonClick(e);
              onDelete?.(client);
            }}
            size="icon"
            variant="destructive"
            title={t('clients.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ClientActions;
